import { Controller } from 'stimulus'

/**
 * Manages javascript for a property address entry
 *
 * @class PropertyAddressController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'city',
    'deleteIcon',
    'errorList',
    'id',
    'input',
    'moveIn',
    'state',
    'streetAddress',
    'zip'
  ]

  /**
   * @function controllers.PropertyAddressController.blurMoveIn
   * @memberof controllers
   * @description handles blur event for move in field
   *
   * @param {Event} event
   *
   * @return {undefined}
   */
  async blurMoveIn(event) {
    if (event.target.value.length !== 7) {
      this.element.dispatchEvent(new CustomEvent('toggleNext', { detail: [ false ] }))
      event.target.dispatchEvent(new CustomEvent('spawnErrors', { detail: [ 'Date must be in mm/yyyy format' ] }))
      this.moveInTarget.scrollIntoView({ block: 'start', inline: 'start', behavior: 'smooth' })
      return
    } else {
      this._removeErrorsFromField(event.target)
      await this._submitProperty()
      this._triggerCalculation()
    }
  }

  /**
   * @function controllers.PropertyAddressController.handleMoveIn
   * @memberof controllers
   * @description handles keydown events for move in field
   *
   * @param {Event} event
   *
   * @return {undefined}
   */
  handleMoveIn(event) {
    clearTimeout(this.moveInTimeout)

    this.moveInTimeout = setTimeout(() => {
      if (event.keyCode === 8) {
        return
      } else if (event.target.value.length === 7) {
        if (this.formFilled) {
          this._removeErrorsFromField(event.target)
          this._submitProperty()
        }
      }
    }, 500)
  }

  /**
   * @function controllers.PropertyAddressController.clearErrorFields
   * @memberof controllers
   * @description handles input events for most inputs
   *
   * @param {Event} event
   *
   * @return {undefined}
   */
  clearErrorFields(event) {
    clearTimeout(this.errorTimeout)

    this.errorTimeout = setTimeout(() => {
      if (event.target.value.length >= 1) {
        this._removeErrorsFromField(event.target)
      }

      if (this.formFilled) {
        this._submitProperty()
      }
    }, 500)
  }

  /**
   * @function controllers.PropertyAddressController.deleteProperty
   * @memberof controllers
   * @description handles deletion of fieldset
   *
   *
   * @return {undefined}
   */
  async deleteProperty() {
    if (this.hasIdTarget) {
      await this._triggerBackendDeletion()
      this.element.remove()
      this._triggerCalculation()
    }
  }

  // Private

  /**
   * @function controllers.PropertyAddressController._addErrorFields
   * @memberof controllers
   * @description handles adding error fields to inputs
   * @private
   *
   * @param {Object} errors
   *
   * @return {undefined}
   */
  _addErrorFields(errors) {
    this.errorListTargets.forEach(errorField => {
      const fieldName = this._stripPropertyAttribute(errorField.id)
      const input = this.inputTargets.filter(input => input.id.includes(fieldName))[0]

      if (errors[fieldName]) {
        input.dispatchEvent(new CustomEvent('spawnErrors', { detail: errors[fieldName] }))
      } else {
        return
      }
    })
    this.element.scrollIntoView({ block: 'start', inline: 'start', behavior: 'smooth' })
  }

  /**
   * @function controllers.PropertyAddressController._addPropertyId
   * @memberof controllers
   * @description handles adding hidden ID input
   * @private
   *
   * @param {Number} id
   *
   * @return {undefined}
   */
  _addPropertyId(id) {
    const idInput = document.createElement('input')

    idInput.type = 'hidden'
    idInput.value = id
    idInput.dataset.target = 'borrower-address.propertyId property-address.id property-address.input'
    idInput.id = 'property_id_' + id
    this.element.appendChild(idInput)
  }

  /**
   * @function controllers.PropertyAddressController._removeErrorsFromField
   * @memberof controllers
   * @description removes errors from an input field
   * @private
   *
   * @param {Element} field
   *
   * @return {undefined}
   */
  _removeErrorsFromField(field) {
    field.dispatchEvent(new CustomEvent('removeErrors'))
  }

  /**
   * @function controllers.PropertyAddressController._stripPropertyAttribute
   * @memberof controllers
   * @description strips property attribute name
   * @private
   *
   * @param {String} attributeName
   *
   * @return {String} strippedAttribute
   */
  _stripPropertyAttribute(attributeName) {
    let strippedAttribute
    if (attributeName.includes('move_in_on')) {
      strippedAttribute = 'move_in_on'
    } else {
      strippedAttribute = attributeName.split('_')[1]
    }
    return strippedAttribute
  }

  /**
   * @function controllers.PropertyAddressController._submitProperty
   * @memberof controllers
   * @description submits property to backend
   * @private
   *
   * @return {undefined}
   */
  async _submitProperty() {
    const borrowerId = this.data.get('borrower')
    const property = this.propertyData
    const data = { borrower_id: borrowerId, property }
    let result

    if (this.persistedProperty) {
      result = await fetch(`/v1/borrower_addresses/${this.idTarget.value}`, {
        method:      'PATCH',
        credentials: 'same-origin',
        headers:     { 'content-type': 'application/json' },
        mode:        'cors',
        body:        JSON.stringify(data)
      })
    } else {
      result = await fetch('/v1/borrower_addresses', {
        method:      'POST',
        credentials: 'same-origin',
        headers:     { 'content-type': 'application/json' },
        mode:        'cors',
        body:        JSON.stringify(data)
      })
    }
    const json = await result.json()
    if (result.status === 422) {
      this._addErrorFields(json.errors)
      this.element.dispatchEvent(new CustomEvent('toggleNext', { detail: [ false ] }))
    } else {
      if (!this.hasIdTarget) {
        this._addPropertyId(json.id)
      }
      this._triggerCalculation()
    }
  }

  /**
   * @function controllers.PropertyAddressController._triggerBackendDeletion
   * @memberof controllers
   * @description sends delete request to backend for property
   * @private
   *
   * @return {Bool} true or false
   */
  async _triggerBackendDeletion() {
    const result = await fetch(`/v1/borrower_addresses/${this.idTarget.value}`, {
      method:      'DELETE',
      credentials: 'same-origin',
      headers:     { 'content-type': 'application/json' },
      mode:        'cors'
    })

    if (result.status === 204) {
      return true
    } else {
      return false
    }
  }

  /**
   * @function controllers.PropertyAddressController._triggerCalculation
   * @memberof controllers
   * @description triggers the calculation of history length in the borrower-address controller
   * @private
   *
   * @return {undefined}
   */
  _triggerCalculation() {
    this.moveInTarget.dispatchEvent(new CustomEvent('calculate'))
  }

  /**
   * @function controllers.PropertyAddressController.persistedProperty
   * @memberof controllers
   * @description checks whether property is persisted
   * @private
   *
   * @return {Bool} true or false
   */
  get persistedProperty() {
    return this.hasIdTarget
  }

  /**
   * @function controllers.PropertyAddressController.propertyData
   * @memberof controllers
   * @description returns property data values
   * @private
   *
   * @return {Object} data
   */
  get propertyData() {
    const data = {}
    this.inputTargets.map(input =>
      data[this._stripPropertyAttribute(input.id)] = input.value
    )
    return data
  }

  /**
   * @function controllers.PropertyAddressController.formFilled
   * @memberof controllers
   * @description checks whether all inputs have values
   * @private
   *
   * @return {Bool} true or false
   */
  get formFilled() {
    return this.inputTargets.every(field => field.value.length > 0)
  }
}
